import React, { useEffect, useRef, useCallback } from "react";
import ReactECharts from "echarts-for-react";
import { transparentize } from "../../utils/colorHelper";
import { formatNumberWithCommas, formatToTwoDecimalPlaces } from "../../utils/numberFormatter.js";

const LineBarChart = ({ data, options, legendField, xAxisField, yAxisField }) => {
  const chartRef = useRef(null);

  const adjustDataForSecondYAxis = useCallback((chartData, seriesConfig) => {
    if (!chartData || !seriesConfig) return chartData || [];

    return chartData.map((item) => {
      const config = seriesConfig[item.name];
      if (config && config.yAxisIndex === 1 && item.value !== "-" && item.value !== null && item.value !== undefined) {
        const adjustedItem = { ...item };
        adjustedItem.value = item.value >= 1 ? item.value - 1 : -(1 - item.value);
        return adjustedItem;
      }
      return { ...item };
    });
  }, []);

  const hasSecondYAxis = Object.values(options).some((config) => config.yAxisIndex === 1);

  const generateSeries = (chartData, seriesConfig) => {
    const xData = [...new Set(chartData.map((d) => d[xAxisField]))].sort((a, b) => new Date(a) - new Date(b));
    const seriesMap = {};

    let hasBar = Object.values(seriesConfig).filter((v) => v.type === "bar").length > 0;

    Object.keys(seriesConfig).forEach((name) => {
      seriesMap[name] = {
        name,
        type: seriesConfig[name].type || "line",
        yAxisIndex: seriesConfig[name].yAxisIndex || 0,
        symbol: "none",
        itemStyle: { color: hasBar && seriesConfig[name].type !== "bar" ? transparentize(seriesConfig[name].color, 0.5) : seriesConfig[name].color },
        lineStyle: {
          type: name === "Forecast Line" || name === "SPI" || name === "CPI" ? "dashed" : "solid",
        },
        data: Array(xData.length).fill(null),
        barMaxWidth: 10,
      };
      if (seriesConfig[name].markLine) {
        seriesMap[name].markLine = {
          lineStyle: {
            type: seriesConfig[name].markLine.xAxis ? "dashed" : "solid",
            color: seriesConfig[name].markLine.color,
            width: seriesConfig[name].markLine.xAxis ? 1 : 2,
          },
          label: { show: false },
          symbol: seriesConfig[name].markLine.xAxis ? ["none", "triangle"] : "none",
          data: [seriesConfig[name].markLine],
          animation: false,
        };
      }
    });

    // const adjustedChartData = adjustDataForSecondYAxis(chartData, seriesConfig);

    // adjustedChartData.forEach((item) => {
    chartData.forEach((item) => {
      const dataIndex = xData.indexOf(item[xAxisField]);
      if (dataIndex !== -1 && seriesMap[item.name]) {
        seriesMap[item.name].data[dataIndex] = item.value === "-" ? null : item.value;
      }
    });

    return Object.values(seriesMap);
  };

  const getRange = (chartData, seriesConfig) => {
    let values = [];
    Object.keys(seriesConfig).forEach((name) => {
      if (seriesConfig[name].yAxisIndex === 1) {
        values.push(chartData.filter((d) => d[legendField] === name).map((d) => (d[yAxisField] >= 1 ? d[yAxisField] - 1 : -(1 - d[yAxisField]))));
      }
    });
    values = values.flat();
    return Math.max(Math.abs(Math.max(...values)), Math.abs(Math.min(...values)));
  };

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        label: { backgroundColor: "#6a7985" },
        lineStyle: { type: "dashed" },
      },
      formatter: function (params) {
        let tooltipText = "";
        params.forEach((param) => {
          const isOnSecondYAxis = options[param.seriesName]?.yAxisIndex === 1;
          if (param.value !== null && param.value !== undefined) {
            let originalValue = param.value;
            if (isOnSecondYAxis) {
              // originalValue = param.value >= 0 ? param.value + 1 : 1 - Math.abs(param.value);
              originalValue = formatToTwoDecimalPlaces(param.value);
            } else {
              originalValue = formatNumberWithCommas(param.value);
            }
            const formattedValue = originalValue;
            tooltipText += `${param.seriesName}: ${formattedValue}<br/>`;
          }
        });
        return tooltipText;
      },
    },
    legend: { data: [...new Set(data.map((d) => d[legendField]))], right: "30px", top: "10px" },
    xAxis: {
      type: "category",
      boundaryGap: true,
      axisTick: { show: false },
      axisLine: { show: true, lineStyle: { color: "#D0D5DE" } },
      axisLabel: { color: "#717273", fontSize: 12 },
      data: (() => {
        const xData = [...new Set(data.map((d) => d[xAxisField]))];
        return xData.sort((a, b) => new Date(a) - new Date(b));
      })(),
    },
    yAxis: [
      {
        type: yAxisField,
        axisLine: { show: true, lineStyle: { color: "#D0D5DE" } },
        axisLabel: { color: "#717273", fontSize: 12 },
        splitLine: { show: true, lineStyle: { color: "#D0D5DE" } },
      },
      ...(hasSecondYAxis
        ? [
          {
            type: yAxisField,
            position: "right",
            axisLine: { show: true, lineStyle: { color: "#D0D5DE" } },
            axisLabel: {
              color: "#717273",
              fontSize: 12,
              // formatter: function (value) {
              //   if (Number.isInteger(value)) {
              //     return value + 1;
              //   } else {
              //     return "";
              //   }
              //   return value;
              // },
            },
            splitLine: { show: true, lineStyle: { color: "#D0D5DE" } },
            // min: getRange(data, options) > 1 ? -getRange(data, options) : -1,
            // max: getRange(data, options) > 1 ? getRange(data, options) : 1,
            scale: false,
            axisPointer: {
              label: {
                show: true,
                // formatter: function (params) {
                //   if (params.axisDimension === "y" && params.valueAxisIndex === 1) {
                //     return (params.value >= 0 ? params.value + 1 : 1 - Math.abs(params.value)).toFixed(2);
                //   } else {
                //     return params.value;
                //   }
                // },
              },
            },
          },
        ]
        : []),
    ],
    grid: { left: "80px", right: hasSecondYAxis ? "30px" : "20px", bottom: "30px", top: "45px" },
    series: generateSeries(data, options),
  };

  useEffect(() => {
    if (chartRef.current && options) {
      chartRef.current.getEchartsInstance().setOption(option, true);
    }
  }, [data, options, legendField, xAxisField, yAxisField]);

  return <ReactECharts ref={chartRef} option={option} style={{ width: "100%", height: "100%" }} />;
};

export default LineBarChart;
